import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AuthService } from '@features/auth/data-access/auth.service';
import { IntegrationCapability } from '../interfaces/integration-capability.enum';
import { IntegrationConnectionStatus } from '../interfaces/integration-connection-status.enum';
import { IntegrationProviderSlug } from '../interfaces/integration-provider-slug.enum';
import { IntegrationStatus } from '../interfaces/integration-status.enum';
import { IntegrationsApiService } from './integrations-api.service';
import { IIntegrationConnectionApiResponse } from './interfaces/integration-api.interface';

@Injectable()
export class IntegrationsFacade {
  private readonly authService = inject(AuthService);
  private readonly integrationsApi = inject(IntegrationsApiService);
  private loadPromise: Promise<boolean> | null = null;

  public readonly canManageIntegrations = computed(() =>
    this.authService.scopes().includes('integrations.manage'),
  );

  public readonly connectionsByProvider = signal<
    Readonly<Partial<Record<IntegrationProviderSlug, IIntegrationConnectionApiResponse>>>
  >({});

  public readonly liveStatuses = computed(
    (): Readonly<Record<IntegrationProviderSlug, IntegrationStatus>> => {
      const connections = this.connectionsByProvider();
      const statuses = {} as Record<IntegrationProviderSlug, IntegrationStatus>;
      for (const provider of Object.values(IntegrationProviderSlug)) {
        statuses[provider] = this.mapConnectionToCatalogStatus(connections[provider] ?? null);
      }
      return statuses;
    },
  );

  public readonly loadFailed = signal(false);

  public constructor() {
    effect(() => {
      void this.canManageIntegrations();
      void this.loadConnections();
    });
  }

  public isLiveProvider(id: string): id is IntegrationProviderSlug {
    return (Object.values(IntegrationProviderSlug) as string[]).includes(id);
  }

  public statusFor(provider: IntegrationProviderSlug): IntegrationStatus {
    return this.mapConnectionToCatalogStatus(this.connectionsByProvider()[provider] ?? null);
  }

  public connectionFor(
    provider: IntegrationProviderSlug,
  ): IIntegrationConnectionApiResponse | null {
    return this.connectionsByProvider()[provider] ?? null;
  }

  public async loadConnections(): Promise<boolean> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.fetchConnections().finally(() => {
      this.loadPromise = null;
    });
    return this.loadPromise;
  }

  public async disconnect(provider: IntegrationProviderSlug): Promise<boolean> {
    const connectionId = this.connectionsByProvider()[provider]?.id;
    if (!connectionId || !this.canManageIntegrations()) {
      return false;
    }

    await this.integrationsApi.disconnect(connectionId);
    await this.loadConnections();
    return true;
  }

  private async fetchConnections(): Promise<boolean> {
    if (!this.canManageIntegrations()) {
      this.connectionsByProvider.set({});
      this.loadFailed.set(false);
      return true;
    }

    try {
      const connections = await this.integrationsApi.listConnections();
      const byProvider: Partial<
        Record<IntegrationProviderSlug, IIntegrationConnectionApiResponse>
      > = {};

      for (const connection of connections) {
        if (
          connection.status === IntegrationConnectionStatus.Disconnected ||
          !this.isLiveProvider(connection.provider)
        ) {
          continue;
        }
        byProvider[connection.provider] = connection;
      }

      this.connectionsByProvider.set(byProvider);
      this.loadFailed.set(false);
      return true;
    } catch {
      this.connectionsByProvider.set({});
      this.loadFailed.set(true);
      return false;
    }
  }

  private mapConnectionToCatalogStatus(
    connection: IIntegrationConnectionApiResponse | null,
  ): IntegrationStatus {
    if (!connection) {
      return IntegrationStatus.NotConnected;
    }

    switch (connection.status) {
      case IntegrationConnectionStatus.Connected:
        return this.requiresChannelSetup(connection) && !this.hasSelectedChannel(connection)
          ? IntegrationStatus.Pending
          : IntegrationStatus.Active;
      case IntegrationConnectionStatus.Error:
      case IntegrationConnectionStatus.ReauthorizationRequired:
        return IntegrationStatus.Pending;
      default:
        return IntegrationStatus.NotConnected;
    }
  }

  private requiresChannelSetup(connection: IIntegrationConnectionApiResponse): boolean {
    return connection.capabilities.includes(IntegrationCapability.Channels);
  }

  private hasSelectedChannel(connection: IIntegrationConnectionApiResponse): boolean {
    const config = connection.config ?? {};
    return Boolean(config['selectedChannel']);
  }
}
